import React, { useRef, useState } from 'react';
import { Check, Download, Loader2, Paperclip, Plus, X } from 'lucide-react';
import { FieldConfig, SubmissionFile, TaskField } from '../../types/portal';
import { HAIRLINE } from '../assessment/AssessmentUI';

const fa = (n: number) => n.toLocaleString('fa-IR');

/** مقدار جاری یک فیلد در فرم */
export interface FieldValue {
  textValue?: string;
  numberValue?: number;
  dateValue?: string;
  jsonValue?: any;
  files: SubmissionFile[];
}

export const emptyValue = (): FieldValue => ({ files: [] });

const inputClass =
  'w-full rounded-xl border bg-white px-3 py-2.5 text-[13px] text-slate-800 outline-none transition focus:border-[#9DB0E4]';

/** فرمت خوانای حجم فایل */
function humanSize(bytes: number): string {
  if (bytes < 1024) return fa(bytes) + ' بایت';
  if (bytes < 1024 * 1024) return fa(Math.round(bytes / 1024)) + ' کیلوبایت';
  return fa(Math.round((bytes / (1024 * 1024)) * 10) / 10) + ' مگابایت';
}

interface Props {
  field: TaskField;
  value: FieldValue;
  onChange: (patch: Partial<FieldValue>) => void;
  onUpload: (file: File) => Promise<void>;
  onRemoveFile: (fileId: string) => Promise<void>;
  onDownloadFile: (file: SubmissionFile) => void;
  disabled?: boolean;
}

/** بخش فایل — بین FILE، IMAGE، VIDEO، AUDIO و TEMPLATE_UPLOAD مشترک است */
const FileArea: React.FC<Props & { accept?: string }> = ({
  field,
  value,
  onUpload,
  onRemoveFile,
  onDownloadFile,
  disabled,
  accept,
}) => {
  const config: FieldConfig = field.config ?? {};
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const limit = config.multiple ? (config.maxFiles ?? 10) : 1;
  const full = value.files.length >= limit;

  const pick = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    setError('');
    try {
      for (const file of Array.from(files).slice(0, limit - value.files.length)) {
        await onUpload(file);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'آپلود انجام نشد.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={!!config.multiple}
        className="hidden"
        onChange={(e) => pick(e.target.files)}
      />

      {value.files.length ? (
        <div className="mb-2 space-y-1.5">
          {value.files.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-2 rounded-xl border px-3 py-2"
              style={{ borderColor: HAIRLINE }}
            >
              <Paperclip className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span className="min-w-0 flex-1 truncate text-[12px] font-bold text-slate-700">
                {file.originalName}
              </span>
              <span className="shrink-0 text-[10.5px] font-bold text-slate-400">
                {humanSize(file.sizeBytes)}
              </span>
              <button
                type="button"
                onClick={() => onDownloadFile(file)}
                className="shrink-0 rounded-lg p-1 text-slate-400 hover:text-[#1C3B9B]"
                aria-label="دانلود"
              >
                <Download className="h-3.5 w-3.5" />
              </button>
              {!disabled ? (
                <button
                  type="button"
                  onClick={() => onRemoveFile(file.id)}
                  className="shrink-0 rounded-lg p-1 text-red-500 hover:bg-red-50"
                  aria-label="حذف"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {!disabled && !full ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed px-3 py-3 text-[12px] font-black text-[#1C3B9B] transition hover:bg-[#F7FAFF] disabled:opacity-50"
          style={{ borderColor: '#C8D6FF' }}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {busy ? 'در حال آپلود…' : 'انتخاب فایل'}
        </button>
      ) : null}

      <p className="mt-1.5 text-[10.5px] font-bold text-slate-400">
        {config.acceptedFormats?.length ? 'فرمت مجاز: ' + config.acceptedFormats.join('، ') : ''}
        {config.maxSizeMb ? ' · حداکثر ' + fa(config.maxSizeMb) + ' مگابایت' : ''}
        {limit > 1 ? ' · تا ' + fa(limit) + ' فایل' : ''}
      </p>

      {error ? <p className="mt-1 text-[11px] font-black text-red-600">{error}</p> : null}
    </div>
  );
};

/**
 * ورودی یک فیلد تسک.
 *
 * همان قواعدی که سرور اعمال می‌کند اینجا هم راهنمایی می‌شوند (سقف کاراکتر،
 * بازهٔ عدد، تعداد گزینه)، ولی اعتبارسنجی واقعی روی سرور است؛ این فقط برای
 * این است که کاربر پیش از ارسال بداند چه می‌خواهیم.
 */
export const TaskFieldInput: React.FC<Props> = (props) => {
  const { field, value, onChange, disabled } = props;
  const config: FieldConfig = field.config ?? {};

  const body = (() => {
    switch (field.kind) {
      case 'SHORT_TEXT':
        return (
          <input
            value={value.textValue ?? ''}
            disabled={disabled}
            maxLength={config.maxChars}
            onChange={(e) => onChange({ textValue: e.target.value })}
            className={inputClass}
            style={{ borderColor: HAIRLINE }}
          />
        );

      case 'LONG_TEXT':
      case 'PEER_FEEDBACK':
        return (
          <>
            <textarea
              rows={5}
              value={value.textValue ?? ''}
              disabled={disabled}
              maxLength={config.maxChars}
              onChange={(e) => onChange({ textValue: e.target.value })}
              className={inputClass}
              style={{ borderColor: HAIRLINE }}
            />
            {config.minChars || config.maxChars ? (
              <p className="mt-1 text-[10.5px] font-bold text-slate-400">
                {fa((value.textValue ?? '').length)}
                {config.maxChars ? ' از ' + fa(config.maxChars) : ''} کاراکتر
                {config.minChars ? ' · حداقل ' + fa(config.minChars) : ''}
              </p>
            ) : null}
          </>
        );

      case 'LINK':
        return (
          <input
            type="url"
            dir="ltr"
            placeholder="https://"
            value={value.textValue ?? ''}
            disabled={disabled}
            onChange={(e) => onChange({ textValue: e.target.value })}
            className={inputClass + ' text-left'}
            style={{ borderColor: HAIRLINE }}
          />
        );

      case 'NUMBER':
        return (
          <input
            type="number"
            value={value.numberValue ?? ''}
            disabled={disabled}
            min={config.min}
            max={config.max}
            onChange={(e) =>
              onChange({ numberValue: e.target.value === '' ? undefined : Number(e.target.value) })
            }
            className={inputClass}
            style={{ borderColor: HAIRLINE }}
          />
        );

      case 'DATE':
        return (
          <input
            type="date"
            value={value.dateValue ? value.dateValue.slice(0, 10) : ''}
            disabled={disabled}
            onChange={(e) => onChange({ dateValue: e.target.value || undefined })}
            className={inputClass}
            style={{ borderColor: HAIRLINE }}
          />
        );

      case 'SINGLE_SELECT':
        return (
          <div className="space-y-1.5">
            {(config.options ?? []).map((option) => {
              const active = value.textValue === option;
              return (
                <button
                  key={option}
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange({ textValue: option })}
                  className={
                    'flex w-full items-center gap-2.5 rounded-xl border px-3 py-2.5 text-right text-[12.5px] font-bold transition ' +
                    (active ? 'border-[#1C3B9B] bg-[#F4F7FF] text-slate-900' : 'text-slate-600')
                  }
                  style={active ? undefined : { borderColor: HAIRLINE }}
                >
                  <span
                    className={
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ' +
                      (active ? 'border-[#1C3B9B] bg-[#1C3B9B]' : 'border-slate-300')
                    }
                  >
                    {active ? <Check className="h-2.5 w-2.5 text-white" /> : null}
                  </span>
                  {option}
                </button>
              );
            })}
          </div>
        );

      case 'MULTI_SELECT':
      case 'CHECKLIST': {
        const items = field.kind === 'CHECKLIST' ? (config.items ?? []) : (config.options ?? []);
        const picked: string[] = Array.isArray(value.jsonValue) ? value.jsonValue : [];
        const toggle = (item: string) => {
          const next = picked.includes(item) ? picked.filter((x) => x !== item) : [...picked, item];
          onChange({ jsonValue: next });
        };
        return (
          <div className="space-y-1.5">
            {items.map((item) => {
              const active = picked.includes(item);
              return (
                <button
                  key={item}
                  type="button"
                  disabled={disabled}
                  onClick={() => toggle(item)}
                  className={
                    'flex w-full items-center gap-2.5 rounded-xl border px-3 py-2.5 text-right text-[12.5px] font-bold transition ' +
                    (active ? 'border-[#1C3B9B] bg-[#F4F7FF] text-slate-900' : 'text-slate-600')
                  }
                  style={active ? undefined : { borderColor: HAIRLINE }}
                >
                  <span
                    className={
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 ' +
                      (active ? 'border-[#1C3B9B] bg-[#1C3B9B]' : 'border-slate-300')
                    }
                  >
                    {active ? <Check className="h-2.5 w-2.5 text-white" /> : null}
                  </span>
                  {item}
                </button>
              );
            })}
          </div>
        );
      }

      case 'RATING': {
        const min = config.scaleMin ?? 1;
        const max = config.scaleMax ?? 10;
        const steps = Array.from({ length: max - min + 1 }, (_, i) => min + i);
        return (
          <div className="flex flex-wrap gap-1.5">
            {steps.map((step) => {
              const active = value.numberValue === step;
              return (
                <button
                  key={step}
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange({ numberValue: step })}
                  className={
                    'h-10 w-10 rounded-xl border text-[13px] font-black tabular-nums transition ' +
                    (active
                      ? 'border-transparent bg-[#1C3B9B] text-white'
                      : 'bg-white text-slate-500 hover:border-[#9DB0E4]')
                  }
                  style={active ? undefined : { borderColor: HAIRLINE }}
                >
                  {fa(step)}
                </button>
              );
            })}
          </div>
        );
      }

      case 'CONFIRM':
        return (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange({ jsonValue: value.jsonValue !== true })}
            className={
              'flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-3 text-[13px] font-black transition ' +
              (value.jsonValue === true
                ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                : 'text-slate-500')
            }
            style={value.jsonValue === true ? undefined : { borderColor: HAIRLINE }}
          >
            <Check className="h-4 w-4" />
            {value.jsonValue === true ? 'تأیید شد' : 'انجام دادم و تأیید می‌کنم'}
          </button>
        );

      case 'TABLE': {
        const columns = config.columns ?? [];
        const rows: Array<Record<string, string>> = Array.isArray(value.jsonValue)
          ? value.jsonValue
          : [];
        const setRows = (next: Array<Record<string, string>>) => onChange({ jsonValue: next });

        return (
          <div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-right">
                <thead>
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column}
                        className="pb-1.5 text-[11px] font-black text-slate-500"
                      >
                        {column}
                      </th>
                    ))}
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {columns.map((column) => (
                        <td key={column} className="p-0.5">
                          <input
                            value={row[column] ?? ''}
                            disabled={disabled}
                            onChange={(e) =>
                              setRows(
                                rows.map((r, i) =>
                                  i === rowIndex ? { ...r, [column]: e.target.value } : r,
                                ),
                              )
                            }
                            className="w-full rounded-lg border px-2 py-1.5 text-[12px] outline-none focus:border-[#9DB0E4]"
                            style={{ borderColor: HAIRLINE }}
                          />
                        </td>
                      ))}
                      <td className="p-0.5 text-center">
                        {!disabled ? (
                          <button
                            type="button"
                            onClick={() => setRows(rows.filter((_, i) => i !== rowIndex))}
                            className="rounded p-1 text-red-500 hover:bg-red-50"
                            aria-label="حذف ردیف"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!disabled ? (
              <button
                type="button"
                onClick={() =>
                  setRows([...rows, Object.fromEntries(columns.map((c) => [c, '']))])
                }
                className="mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed px-3 py-2 text-[12px] font-black text-[#1C3B9B]"
                style={{ borderColor: '#C8D6FF' }}
              >
                <Plus className="h-3.5 w-3.5" />
                افزودن ردیف
              </button>
            ) : null}

            {config.minRows ? (
              <p className="mt-1 text-[10.5px] font-bold text-slate-400">
                حداقل {fa(config.minRows)} ردیف
              </p>
            ) : null}
          </div>
        );
      }

      case 'IMAGE':
        return <FileArea {...props} accept="image/*" />;
      case 'VIDEO':
        return <FileArea {...props} accept="video/*" />;
      case 'AUDIO':
        return <FileArea {...props} accept="audio/*" />;
      case 'FILE':
      case 'TEMPLATE_UPLOAD':
        return <FileArea {...props} />;

      default:
        return (
          <textarea
            rows={3}
            value={value.textValue ?? ''}
            disabled={disabled}
            onChange={(e) => onChange({ textValue: e.target.value })}
            className={inputClass}
            style={{ borderColor: HAIRLINE }}
          />
        );
    }
  })();

  return (
    <div>
      <label className="mb-1.5 block">
        <span className="text-[13px] font-black text-slate-800">
          {field.label}
          {field.required ? <span className="text-red-500"> *</span> : null}
        </span>
        {field.helpText ? (
          <span className="mt-0.5 block text-[11px] font-bold leading-6 text-slate-400">
            {field.helpText}
          </span>
        ) : null}
      </label>
      {body}
    </div>
  );
};
